import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AttemptMode, Region, StepEvent, StepWithRegions } from '@simultrain/shared';

export type RunnerMode = AttemptMode;

export type Feedback =
  | { kind: 'idle' }
  | { kind: 'good'; text: string }
  | { kind: 'wrong'; stage: 1 | 2 | 3; text: string }
  | { kind: 'corrective'; text: string };

export type ClickPoint = { x: number; y: number }; // normalized 0..1

const DEFAULT_STAGE1 = 'Dit is niet de juiste actie.';

type ClickErrorRegion = Extract<Region, { kind: 'click-error' }>;

type LiteralExpected = Extract<Region, { kind: 'input' }>['expected'] & { kind: 'literal' };

// Applied to both the learner's typed value and the expected value before
// comparing a 'literal' input match — always trims incidental whitespace,
// then applies whichever leniency flags the author turned on (protocol /
// trailing-slash / case) in that order, so e.g. "HTTPS://Voorbeeld.nl/pad/"
// and "voorbeeld.nl/pad" can both satisfy an expected "voorbeeld.nl/pad".
function normalizeForMatch(raw: string, expected: LiteralExpected): string {
  let v = raw.trim();
  if (expected.ignoreProtocol) v = v.replace(/^https?:\/\//i, '');
  if (expected.ignoreTrailingSlash) v = v.replace(/\/$/, '');
  if (!expected.caseSensitive) v = v.toLowerCase();
  return v;
}

type WrongResolution = {
  stage: 1 | 2 | 3;
  text: string;
  reveal: boolean;
};

/**
 * Mistake-feedback loop for a click-error region WITHOUT its own corrective
 * path, or a miss (no region hit at all):
 *   Attempt 1 — region's own `feedback` text, or the generic default for a miss.
 *   Attempt 2 — "feedforward": the step's taskPrompt (repeat the task).
 *   Attempt 3+ — taskPrompt again, and the correct region is visually revealed.
 * Attempt count is capped at 3 — further mistakes keep repeating that tier.
 *
 * A region WITH a corrective path (a real zijstap) never goes through this —
 * see handleHit: it enters the corrective mini-flow immediately and silently,
 * with no feedback text at all. This same three-stage loop then applies
 * *inside* that mini-flow, to mistakes made while trying to complete it or
 * find the way back — using the corrective step's own fresh attempt count.
 */
function resolveWrong(
  step: StepWithRegions,
  region: ClickErrorRegion | null,
  attemptsBefore: number,
): WrongResolution {
  const attemptNumber = Math.min(attemptsBefore + 1, 3);
  // For a miss, fallbackFeedback (if authored) is a miss-specific feedforward —
  // it overrides the generic taskPrompt at stage 2+, never at stage 1.
  const feedforward = (!region && step.fallbackFeedback) || step.taskPrompt || DEFAULT_STAGE1;

  if (attemptNumber === 1) {
    const text = region ? region.feedback || DEFAULT_STAGE1 : step.stageFeedback1 || DEFAULT_STAGE1;
    return { stage: 1, text, reveal: false };
  }
  if (attemptNumber === 2) {
    return { stage: 2, text: feedforward, reveal: false };
  }
  return { stage: 3, text: feedforward, reveal: true };
}

function findRevealTarget(step: StepWithRegions): string | null {
  // When a step has several valid click-correct regions, reveal the one
  // authored as primary (default true) — otherwise this fell back to
  // whichever happened to come first in the array, which could point at an
  // "alternative" action the author never meant to be the featured one.
  return (
    step.regions.find((r) => r.kind === 'click-correct' && r.primary !== false) ??
    step.regions.find((r) => r.kind === 'click-correct' || r.kind === 'input')
  )?.id ?? null;
}

/**
 * Runtime engine for a chapter. Handles step transitions, the mistake-feedback
 * loop, corrective (side-step) flows, and event recording for scoring.
 *
 * Recovery stack: entering a corrective branch pushes the step whose
 * click-error region branched away (the "origin") onto a stack. A zijstap
 * can chain any number of corrective steps via their own successNext, same
 * as main steps; successNext === null on one marks it as that zijstap's
 * exit. Reaching the exit pops the stack, scores the origin's mistake as
 * self_corrected, and continues from the origin's OWN successNext — the
 * interrupted step itself is never revisited, only what would have come
 * after it.
 */
export function useChapterRunner(steps: StepWithRegions[], startId: string | null, mode: RunnerMode) {
  const stepMap = useMemo(() => new Map(steps.map((s) => [s.id, s])), [steps]);

  const [currentStepId, setCurrentStepId] = useState<string | null>(startId);
  // startId arrives async (after chapter loads); set it once when it first becomes non-null.
  useEffect(() => {
    if (startId && !currentStepId) setCurrentStepId(startId);
  }, [startId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [feedback, setFeedback] = useState<Feedback>({ kind: 'idle' });

  // Per-step wrong attempt counter — reset when moving to a genuinely new
  // step, but NOT when returning from a corrective mini-flow (see advanceTo).
  const [wrongAttemptsOnStep, setWrongAttemptsOnStep] = useState(0);
  // Toets: whether this step has already been scored 'wrong' (mistake budget
  // exceeded) — guards against recording it more than once per step visit.
  const [wrongRecordedOnStep, setWrongRecordedOnStep] = useState(false);
  // Region id to visually reveal (stage-3 escalation for a miss / error region
  // without its own corrective path).
  const [revealRegionId, setRevealRegionId] = useState<string | null>(null);

  // Recovery stack: when entering a corrective branch, push the origin main-
  // flow step's id. Supports nested side steps.
  const [recoveryStack, setRecoveryStack] = useState<string[]>([]);

  const [events, setEvents] = useState<StepEvent[]>([]);
  const [finished, setFinished] = useState(false);

  const current = currentStepId ? (stepMap.get(currentStepId) ?? null) : null;

  // Toets and eindtoets are both silent — no hints, no reveals, no zijstap
  // feedback text shown — only the internal per-step mistake budget applies.
  const isTestLike = mode === 'test' || mode === 'final_test';
  const showFeedback = !isTestLike;

  const recordEvent = useCallback(
    (stepId: string, outcome: StepEvent['outcome'], detail?: Record<string, unknown>) => {
      setEvents((prev) => [...prev, { stepId, outcome, timestamp: new Date().toISOString(), detail }]);
    },
    [],
  );

  const advanceTo = useCallback(
    (next: string | null) => {
      setFeedback({ kind: 'idle' });
      setRevealRegionId(null);

      if (!next) {
        setRecoveryStack((stack) => {
          if (stack.length === 0) {
            // End of main chapter.
            setCurrentStepId(null);
            setFinished(true);
            setWrongAttemptsOnStep(0);
            setWrongRecordedOnStep(false);
            return stack;
          }
          // This is the zijstap's exit: the mistake that sent the learner
          // here is now resolved — score it against the origin step, and
          // continue from wherever THAT step's own successNext would have
          // gone. Never revisit the origin step itself, so it gets a fresh
          // slate exactly like any other genuinely new step.
          const [originId, ...rest] = stack;
          const originNext = stepMap.get(originId!)?.successNext ?? null;
          recordEvent(originId!, 'self_corrected');
          setWrongAttemptsOnStep(0);
          setWrongRecordedOnStep(false);
          if (originNext) {
            setCurrentStepId(originNext);
          } else {
            setCurrentStepId(null);
            setFinished(true);
          }
          return rest;
        });
        return;
      }

      // Moving forward to a genuinely new step — fresh counters.
      setWrongAttemptsOnStep(0);
      setWrongRecordedOnStep(false);
      setCurrentStepId(next);
    },
    [recordEvent, stepMap],
  );

  // Toets: exceeding the per-step mistake budget (more than 1 wrong action —
  // a zijstap entry counts as one) scores the whole step 'wrong', regardless
  // of what happens afterwards. Recorded once per step visit.
  const maybeRecordToetsWrong = useCallback(
    (stepId: string, attemptsBefore: number, detail?: Record<string, unknown>) => {
      if (!isTestLike || wrongRecordedOnStep || attemptsBefore + 1 < 2) return;
      recordEvent(stepId, 'wrong', detail);
      setWrongRecordedOnStep(true);
    },
    [isTestLike, wrongRecordedOnStep, recordEvent],
  );

  const handleHit = useCallback(
    (region: Region) => {
      if (!current) return;
      switch (region.kind) {
        case 'click-correct': {
          const next = region.leadsTo ?? current.successNext;
          if (recoveryStack.length > 0) {
            // Inside a corrective mini-flow: its own steps aren't individually
            // scored — only the step it eventually recovers back to is.
            if (showFeedback) setFeedback({ kind: 'good', text: 'Goed gedaan!' });
            advanceTo(next);
            break;
          }
          const outcome: StepEvent['outcome'] =
            wrongAttemptsOnStep === 0 ? 'correct_first_try' : 'self_corrected';
          recordEvent(current.id, outcome, { regionId: region.id });
          if (showFeedback) setFeedback({ kind: 'good', text: 'Goed gedaan!' });
          advanceTo(next);
          break;
        }
        case 'click-error': {
          const attemptsBefore = wrongAttemptsOnStep;
          setWrongAttemptsOnStep((n) => n + 1);

          if (mode === 'instruction') {
            const res = resolveWrong(current, region, attemptsBefore);
            if (showFeedback) setFeedback({ kind: 'wrong', stage: res.stage, text: res.text });
            return;
          }

          maybeRecordToetsWrong(current.id, attemptsBefore, { regionId: region.id });

          if (region.correctivePath.length > 0) {
            // A real zijstap: enter it immediately and silently — a side
            // step is the response to this mistake, not a preceding message.
            // Mistakes made trying to complete/return from it get their own
            // fresh three-stage loop, on the corrective step's own count.
            setRecoveryStack((stack) => [current.id, ...stack]);
            setFeedback({ kind: 'idle' });
            setWrongAttemptsOnStep(0);
            setWrongRecordedOnStep(false);
            setRevealRegionId(null);
            setCurrentStepId(region.correctivePath[0] ?? null);
            break;
          }

          const res = resolveWrong(current, region, attemptsBefore);
          if (showFeedback) setFeedback({ kind: 'wrong', stage: res.stage, text: res.text });
          if (!isTestLike && res.reveal) setRevealRegionId(findRevealTarget(current));
          break;
        }
        case 'input':
          // Input regions are handled by a real <input> element rendered over
          // the rect (see ChapterRunnerPage), which stops click propagation.
          // Reaching here means the click missed that element; nothing to do.
          break;
        case 'drag-source':
          break;
      }
    },
    [advanceTo, current, isTestLike, maybeRecordToetsWrong, mode, recordEvent, recoveryStack.length, showFeedback, wrongAttemptsOnStep],
  );

  const handleMiss = useCallback(() => {
    if (!current) return;
    const attemptsBefore = wrongAttemptsOnStep;
    setWrongAttemptsOnStep((n) => n + 1);
    maybeRecordToetsWrong(current.id, attemptsBefore);
    const res = resolveWrong(current, null, attemptsBefore);
    if (showFeedback) setFeedback({ kind: 'wrong', stage: res.stage, text: res.text });
    if (!isTestLike && res.reveal) setRevealRegionId(findRevealTarget(current));
  }, [current, isTestLike, maybeRecordToetsWrong, showFeedback, wrongAttemptsOnStep]);

  const submitInput = useCallback(
    (region: Region & { kind: 'input' }, value: string) => {
      if (!current) return;
      const expected = region.expected;
      const ok =
        expected.kind === 'literal'
          ? normalizeForMatch(value, expected) === normalizeForMatch(expected.value, expected)
          : new RegExp(expected.pattern, expected.flags).test(value);
      const next = region.leadsTo ?? current.successNext;
      if (ok) {
        if (recoveryStack.length > 0) {
          // Inside a corrective mini-flow: its own steps aren't individually
          // scored — only the step it eventually recovers back to is.
          advanceTo(next);
          return;
        }
        const outcome: StepEvent['outcome'] =
          wrongAttemptsOnStep === 0 ? 'correct_first_try' : 'self_corrected';
        recordEvent(current.id, outcome, { regionId: region.id, value });
        advanceTo(next);
      } else {
        const attemptsBefore = wrongAttemptsOnStep;
        setWrongAttemptsOnStep((n) => n + 1);
        maybeRecordToetsWrong(current.id, attemptsBefore, { regionId: region.id, value });

        const attemptNumber = Math.min(attemptsBefore + 1, 3);
        const stage: 1 | 2 | 3 = attemptNumber as 1 | 2 | 3;
        const text =
          attemptNumber === 1
            ? region.feedbackOnWrong || 'Dat is niet de verwachte invoer.'
            : current.taskPrompt || DEFAULT_STAGE1;
        if (showFeedback) setFeedback({ kind: 'wrong', stage, text });
        if (!isTestLike && attemptNumber === 3) setRevealRegionId(region.id);
      }
    },
    [advanceTo, current, isTestLike, maybeRecordToetsWrong, recordEvent, recoveryStack.length, showFeedback, wrongAttemptsOnStep],
  );

  // Used by instruction mode to advance without recording a scored event.
  const instructionAdvance = useCallback((next: string | null) => {
    advanceTo(next);
  }, [advanceTo]);

  return {
    current,
    feedback,
    finished,
    events,
    inCorrectiveBranch: recoveryStack.length > 0,
    revealRegionId,
    handleHit,
    handleMiss,
    submitInput,
    instructionAdvance,
  };
}
